import { CashFlow } from "components/BooksHomePage/BusinessOverview/CashFlow";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Divider } from "components/design/Divider";
import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import { Chip } from "components/DesignSystem/Chips/Chips";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { Switch } from "components/DesignSystem/Switch/Switch";
import { CaretDown } from "components/icons/CaretDown";
import { SmallInfo } from "components/icons/SmallInfo";
import { EstimatedTaxNudgeBanner } from "components/BooksHomePage/EstimatedTaxNudgeBanner/EstimatedTaxNudgeBanner";
import { ForecastToolbar } from "components/Forecast/ForecastToolbar";

import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { stringify } from "qs";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useFilters } from "hooks/useFilter";
import { useLocalStorage } from "hooks/useLocalStorage";
import {
  useGetAccountsPayableAnalysisQuery,
  useGetAccountsReceivableAnalysisQuery,
  useGetFinancialMetricsQuery,
  useResetFinancialSummaryCacheMutation,
  FinancialMetrics,
  ForecastQueryParams,
} from "store/apis/financialMetrics";
import { useGetStartingDateQuery } from "store/apis/chartOfAccounts";
import { AccountsPayable } from "./AccountsPayable";
import { AccountsReceivable } from "./AccountsReceivable";
import { APAgingCard, ARAgingCard } from "./AgingCard";
import { CashBalance } from "./CashBalance";
import { RevenueAnalysis } from "./RevenueAnalysis";
import { MoMBreakdown } from "./MoMBreakdown";
import { TopCustomers, TopVendors } from "./TopInsights";
import { ArrowDownRed } from "components/icons/ArrowDownRed";
import classNames from "classnames";
import {
  DEFAULT_FORECAST_GROUP_ORDER,
  DEFAULT_FORECAST_VISIBLE_SECTIONS,
  DEFAULT_GROUP_ORDER,
  DEFAULT_VISIBLE_SECTIONS,
  FORECAST_SECTION_GROUPS,
  GROUP_KEYS,
  PERIOD_OPTIONS,
  SectionKey,
  TrendSubtitleProps,
  getAllowedGroupOrder,
  getAllowedVisibleSections,
} from "constants/financialSummary";
import { CustomizeSectionsTrigger } from "./CustomizeSectionsPanel";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import { Formik } from "formik";
import RefreshIcon from "static/images/RefreshIcon.svg";
import { DownloadIcon } from "components/icons/Download";

export const TrendSubtitle = ({
  percentage,
  period,
  direction,
  invertColors = false,
}: TrendSubtitleProps) => {
  if (percentage === null || percentage === undefined) return null;
  const isPositive = direction === "up";
  const isGood = invertColors ? !isPositive : isPositive;
  const colorClass = classNames({
    "t-text-green-80": isGood,
    "t-text-red-70": !isGood,
  });

  return (
    <div className="t-flex t-flex-wrap t-items-center t-gap-1">
      <span className="t-flex t-items-center t-gap-1">
        {!isPositive ? (
          <ArrowDownRed
            className={classNames({
              "t-text-green-80": invertColors,
              "t-text-red-70": !invertColors,
            })}
          />
        ) : (
          <span className="t-rotate-x-180">
            <ArrowDownRed
              className={classNames({
                "t-text-green-80": !invertColors,
                "t-text-red-70": invertColors,
              })}
            />
          </span>
        )}
        <span className={colorClass}>
          {isPositive ? "+" : "-"}
          {Math.abs(percentage)}%
        </span>
      </span>
      <span className="t-text-neutral-40">vs last {period}</span>
    </div>
  );
};

export type MetricData = {
  key: string;
  title: string;
  tooltip: string;
  value: React.ReactNode;
  subtitle: React.ReactNode;
};

export const SummaryMetricsCard = ({
  metrics,
  isFetching,
}: {
  metrics: MetricData[];
  isFetching?: boolean;
}) => (
  <div className="t-flex t-items-stretch t-bg-surface t-border t-border-solid t-border-neutral-10 t-rounded-lg t-py-5 sm:t-grid sm:t-grid-cols-2 sm:t-py-4">
    {metrics.map((metric, index) => (
      <>
        {index > 0 && (
          <Divider
            orientation="vertical"
            type="dashed"
            classNames="t-self-stretch sm:!t-hidden"
          />
        )}
        <div
          key={metric.key}
          className={classNames(
            "t-flex t-flex-col t-gap-2 t-flex-1 t-px-5 sm:t-px-4 sm:t-gap-1 sm:t-min-w-0",
            {
              "sm:t-border-0 sm:t-border-r sm:t-border-dashed sm:t-border-neutral-10":
                index === 0,
              "sm:t-col-span-2 sm:t-border-0 sm:t-border-t sm:t-border-dashed sm:t-border-neutral-10 sm:t-mt-4 sm:t-pt-4":
                index === 2,
            }
          )}
        >
          <div className="t-flex t-items-center t-gap-1">
            <span className="t-text-body-sm t-text-text-30 sm:t-text-caption">
              {metric.title}
            </span>
            <ToolTip text={metric.tooltip}>
              <span className="t-flex t-items-center t-text-text-30 t-cursor-pointer">
                <SmallInfo />
              </span>
            </ToolTip>
          </div>
          <div className="t-text-h4 t-font-semibold t-text-text-100 sm:t-text-subtext-sm">
            {isFetching ? (
              <Skeleton className="t-h-7 t-w-28 sm:t-w-full" />
            ) : (
              metric.value
            )}
          </div>
          <div className="t-text-body-sm sm:t-text-caption">
            {isFetching ? (
              <Skeleton className="t-h-4 t-w-40 sm:t-w-full" />
            ) : (
              metric.subtitle
            )}
          </div>
        </div>
      </>
    ))}
  </div>
);

export const ForecastSubtitle = ({
  value,
  label = "forecast",
  direction = "up",
  invertColors = false,
}: {
  value: React.ReactNode;
  label?: string;
  direction?: "up" | "down";
  invertColors?: boolean;
}) => {
  const isPositive = direction === "up";
  const isGood = invertColors ? !isPositive : isPositive;
  const colorClass = classNames({
    "t-text-dark_green-70": isGood,
    "t-text-red-70": !isGood,
  });

  return (
    <span className={classNames("t-flex t-items-center t-gap-1", colorClass)}>
      {isPositive ? (
        <span className="t-rotate-x-180">
          <ArrowDownRed color="currentColor" size="12" />
        </span>
      ) : (
        <ArrowDownRed color="currentColor" size="12" />
      )}
      <span className="t-text-subtext sm:t-text-caption">{value}</span>
      <span className="t-text-neutral-40 t-text-subtext sm:t-text-caption">
        {label}
      </span>
    </span>
  );
};

export const buildSummaryMetrics = (
  financialMetrics: FinancialMetrics | undefined,
  periodText: string
): MetricData[] => {
  const cashBalancePercentage =
    financialMetrics?.cash_balance.change_percentage ?? null;
  const cashBalanceDirection =
    (cashBalancePercentage ?? 0) >= 0 ? ("up" as const) : ("down" as const);
  const netBurnPercentage =
    financialMetrics?.net_burn_rate.change_percentage ?? null;
  const netBurnDirection =
    (netBurnPercentage ?? 0) >= 0 ? ("up" as const) : ("down" as const);
  const runwayEnd = dayjs().add(financialMetrics?.runway || 0, "month");

  return [
    {
      key: "cash_balance",
      title: "Cash Movement",
      tooltip:
        "Total cash available today across all connected bank accounts. This is the money you can use to run the business right now.",
      value: (
        <AmountSuperScript
          amount={financialMetrics?.cash_balance.amount ?? 0}
        />
      ),
      subtitle: (
        <TrendSubtitle
          percentage={
            cashBalancePercentage !== null
              ? Math.abs(cashBalancePercentage)
              : null
          }
          period={periodText}
          direction={cashBalanceDirection}
        />
      ),
    },
    {
      key: "net_burn_rate",
      title: "Net Burn Rate",
      tooltip:
        "The amount of cash your business spends each month after accounting for all income",
      value: (
        <AmountSuperScript
          amount={financialMetrics?.net_burn_rate.amount ?? 0}
        />
      ),
      subtitle: (
        <TrendSubtitle
          percentage={
            netBurnPercentage !== null ? Math.abs(netBurnPercentage) : null
          }
          period={periodText}
          direction={netBurnDirection}
          invertColors
        />
      ),
    },
    {
      key: "runway",
      title: "Runway",
      tooltip:
        "Estimated time your cash will last if spending continues at the current burn rate.",
      value:
        financialMetrics?.gross_burn_rate === 0 &&
        financialMetrics?.runway === 0 ? (
          "N/A"
        ) : (financialMetrics?.net_burn_rate.amount ?? 0) < 0 ? (
          <>🎉 Infinite</>
        ) : (
          <span>{runwayEnd.format("MMM YYYY")}</span>
        ),
      subtitle: (
        <span className="t-text-neutral-40">
          {(financialMetrics?.net_burn_rate.amount ?? 0) < 0
            ? "You're profitable!"
            : `${financialMetrics?.runway || 0} months`}
        </span>
      ),
    },
  ];
};

type FinancialSummaryFilterType = {
  START_DATE: string;
  END_DATE: string;
  PERIOD: string;
  FORECAST_ENABLED: boolean;
  FORECAST_START: string;
  FORECAST_END: string;
  FORECAST_SET_ID: string | null;
};

const getDateRangeFromPeriod = (period: string) => {
  const monthsMap: Record<string, number> = {
    "3_months": 3,
    "6_months": 6,
    "12_months": 12,
  };

  const months = monthsMap[period] || 6;
  const endDate = dayjs().subtract(1, "month").endOf("month");
  const startDate = endDate.subtract(months - 1, "month").startOf("month");

  return {
    startDate: startDate.format(YYYY_MM_DD),
    endDate: endDate.format(YYYY_MM_DD),
  };
};
const defaultDates = getDateRangeFromPeriod("6_months");

const STORAGE_KEY = "financial_summary_visible_sections";
const GROUP_ORDER_KEY = "financial_summary_group_order";
const FORECAST_STORAGE_KEY = "financial_summary_forecast_visible_sections";
const FORECAST_GROUP_ORDER_KEY = "financial_summary_forecast_group_order";

export const FinancialSummary = () => {
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();

  const { data: startDateData } = useGetStartingDateQuery(
    { groupId, entityId },
    { skip: !groupId || !entityId }
  );
  const bookkeepingStartDate = startDateData?.start_date;

  const { localData: visibleSections, setItem: setVisibleSections } =
    useLocalStorage<Partial<Record<SectionKey, boolean>>>(
      STORAGE_KEY,
      DEFAULT_VISIBLE_SECTIONS
    );
  const { localData: groupOrder, setItem: setGroupOrder } = useLocalStorage<
    string[]
  >(GROUP_ORDER_KEY, DEFAULT_GROUP_ORDER);

  const {
    localData: forecastVisibleSections,
    setItem: setForecastVisibleSections,
  } = useLocalStorage<Partial<Record<SectionKey, boolean>>>(
    FORECAST_STORAGE_KEY,
    DEFAULT_FORECAST_VISIBLE_SECTIONS
  );
  const { localData: forecastGroupOrder, setItem: setForecastGroupOrder } =
    useLocalStorage<string[]>(
      FORECAST_GROUP_ORDER_KEY,
      DEFAULT_FORECAST_GROUP_ORDER
    );

  const {
    values: filter,
    updateFilter,
    updateMultipleFilters,
  } = useFilters<FinancialSummaryFilterType>({
    initialValue: {
      START_DATE: defaultDates.startDate,
      END_DATE: defaultDates.endDate,
      PERIOD: "6_months",
      FORECAST_ENABLED: false,
      FORECAST_START: "",
      FORECAST_END: "",
      FORECAST_SET_ID: null,
    },
    useQueryParams: true,
  });

  const currentPeriod = filter.PERIOD;
  const isForecastEnabled = filter.FORECAST_ENABLED;

  const activeVisibleSections = getAllowedVisibleSections(
    isForecastEnabled ? forecastVisibleSections : visibleSections,
    isForecastEnabled
  );
  const activeGroupOrder = getAllowedGroupOrder(
    isForecastEnabled ? forecastGroupOrder : groupOrder,
    isForecastEnabled
  );

  const handleForecastToggle = (checked: boolean) => {
    if (!checked) {
      updateFilter("FORECAST_ENABLED", checked);
      return;
    }

    const defaultForecastStart = dayjs(filter.END_DATE)
      .add(1, "month")
      .startOf("month")
      .format(YYYY_MM_DD);
    const defaultForecastEnd = dayjs().endOf("year").format(YYYY_MM_DD);

    updateMultipleFilters({
      FORECAST_ENABLED: checked,
      FORECAST_START: filter.FORECAST_START || defaultForecastStart,
      FORECAST_END: filter.FORECAST_END || defaultForecastEnd,
    });
  };

  const handleCustomizeSave = (
    newVisible: Partial<Record<SectionKey, boolean>>,
    newOrder: string[]
  ) => {
    if (isForecastEnabled) {
      setForecastVisibleSections(newVisible);
      setForecastGroupOrder(newOrder);
      return;
    }
    setVisibleSections(newVisible);
    setGroupOrder(newOrder);
  };

  const forecastParams: ForecastQueryParams = isForecastEnabled
    ? {
        assumption_set_id: filter.FORECAST_SET_ID ?? undefined,
        forecast_start_date: filter.FORECAST_START || undefined,
        forecast_end_date: filter.FORECAST_END || undefined,
      }
    : {};

  const {
    data: financialMetrics,
    isLoading: isFinancialMetricsLoading,
    isFetching: isFinancialMetricsFetching,
  } = useGetFinancialMetricsQuery(
    {
      entityId,
      start_date: filter.START_DATE,
      end_date: filter.END_DATE,
      is_custom_date_range: currentPeriod === "custom" || undefined,
    },
    { skip: !entityId || isForecastEnabled }
  );

  const {
    data: arData,
    isLoading: arLoading,
    isSuccess: arSuccess,
    isError: arError,
  } = useGetAccountsReceivableAnalysisQuery(
    { entityId, start_date: filter.START_DATE, end_date: filter.END_DATE },
    { skip: !entityId || !activeVisibleSections.accountsReceivable }
  );

  const {
    data: apData,
    isLoading: apLoading,
    isSuccess: apSuccess,
    isError: apError,
  } = useGetAccountsPayableAnalysisQuery(
    { entityId, start_date: filter.START_DATE, end_date: filter.END_DATE },
    { skip: !entityId || !activeVisibleSections.accountsPayable }
  );

  const [resetFinancialSummaryCache, { isLoading: isResettingCache }] =
    useResetFinancialSummaryCacheMutation();

  const handleRefresh = async () => {
    if (!entityId) return;
    await resetFinancialSummaryCache({ entityId });
  };

  const handlePeriodChange = (period: string) => {
    updateFilter("PERIOD", period);
    if (period === "custom") return;
    const { startDate, endDate } = getDateRangeFromPeriod(period);
    updateFilter("START_DATE", startDate);
    updateFilter("END_DATE", endDate);
  };

  const handleCustomStartDateChange = (date: Date) => {
    updateFilter("PERIOD", "custom");
    updateFilter("START_DATE", dayjs(date).format(YYYY_MM_DD));
  };

  const handleCustomEndDateChange = (date: Date) => {
    updateFilter("PERIOD", "custom");
    updateFilter("END_DATE", dayjs(date).format(YYYY_MM_DD));
  };

  const comboboxValue = PERIOD_OPTIONS.find(
    (opt) => opt.value === currentPeriod
  );

  const periodMonths =
    dayjs(filter.END_DATE).diff(dayjs(filter.START_DATE), "month") + 1;
  const periodText =
    currentPeriod === "custom"
      ? `${periodMonths} months`
      : (
          PERIOD_OPTIONS.find((opt) => opt.value === currentPeriod)?.label ||
          "Last 6 months"
        )
          .toLowerCase()
          .replace("last ", "");

  const isFetchingMetrics =
    isFinancialMetricsFetching || isFinancialMetricsLoading;
  const metrics = buildSummaryMetrics(financialMetrics, periodText);

  const handleExportClick = () => {
    const revenueAnalysisType =
      localStorage.getItem("financial_analysis_type_REVENUE") ===
      "CLASSIFICATION"
        ? "CLASSIFICATION"
        : "CATEGORY";
    const spendAnalysisType =
      localStorage.getItem("financial_analysis_type_SPEND") === "CLASSIFICATION"
        ? "CLASSIFICATION"
        : "CATEGORY";

    const search = stringify({
      entity: entityId,
      ...filter,
      ...activeVisibleSections,
      groupOrder: activeGroupOrder.join(","),
      periodText,
      groupId,
      revenueAnalysisType,
      spendAnalysisType,
    });
    window.open(`/financial-summary-export?${search}`, "_blank");
  };

  const renderGroup = (groupKey: string) => {
    switch (groupKey) {
      case GROUP_KEYS.cashHealth:
        return (
          <>
            {activeVisibleSections.overview && (
              <SummaryMetricsCard
                isFetching={isFetchingMetrics}
                metrics={metrics}
              />
            )}
            <EstimatedTaxNudgeBanner />
            {activeVisibleSections.cashBalance && (
              <CashBalance
                startDate={filter.START_DATE}
                endDate={filter.END_DATE}
              />
            )}
          </>
        );

      case GROUP_KEYS.profitLoss:
        return activeVisibleSections.profitability ? (
          <CashFlow
            categorisationType="ALL"
            period={{
              startDate: filter.START_DATE,
              endDate: filter.END_DATE,
              periodText,
              isCustomDateRange: currentPeriod === "custom",
            }}
            {...forecastParams}
          />
        ) : null;

      case GROUP_KEYS.revenue:
        return (
          <>
            {activeVisibleSections.revenueAnalysis && (
              <RevenueAnalysis
                startDate={filter.START_DATE}
                endDate={filter.END_DATE}
                periodText={periodText}
                reportType="REVENUE"
                isCustomDateRange={currentPeriod === "custom"}
              />
            )}
            {isForecastEnabled && activeVisibleSections.revenueBreakdown && (
              <div className="t-h-96">
                <MoMBreakdown
                  startDate={filter.START_DATE}
                  endDate={filter.END_DATE}
                  reportType="REVENUE"
                  title="MoM Revenue Breakdown"
                  {...forecastParams}
                />
              </div>
            )}
            {!isForecastEnabled &&
              (activeVisibleSections.revenueBreakdown ||
                activeVisibleSections.topCustomers) && (
                <div className="t-flex t-gap-6 t-h-96 sm:t-flex-col sm:t-h-auto">
                  {activeVisibleSections.revenueBreakdown && (
                    <div className="t-flex-1 sm:t-h-96">
                      <MoMBreakdown
                        startDate={filter.START_DATE}
                        endDate={filter.END_DATE}
                        reportType="REVENUE"
                        title="MoM Revenue Breakdown"
                      />
                    </div>
                  )}
                  {activeVisibleSections.topCustomers && (
                    <div className="t-flex-1 sm:t-h-96">
                      <TopCustomers
                        startDate={filter.START_DATE}
                        endDate={filter.END_DATE}
                      />
                    </div>
                  )}
                </div>
              )}
          </>
        );

      case GROUP_KEYS.expense:
        return (
          <>
            {activeVisibleSections.spendAnalysis && (
              <RevenueAnalysis
                startDate={filter.START_DATE}
                endDate={filter.END_DATE}
                periodText={periodText}
                reportType="SPEND"
                isCustomDateRange={currentPeriod === "custom"}
              />
            )}
            {isForecastEnabled && activeVisibleSections.expenseBreakdown && (
              <div className="t-h-96">
                <MoMBreakdown
                  startDate={filter.START_DATE}
                  endDate={filter.END_DATE}
                  reportType="EXPENSE"
                  title="MoM Expense Breakdown"
                  {...forecastParams}
                />
              </div>
            )}
            {!isForecastEnabled &&
              (activeVisibleSections.expenseBreakdown ||
                activeVisibleSections.topVendors) && (
                <div className="t-flex t-gap-6 t-h-96 sm:t-flex-col sm:t-h-auto">
                  {activeVisibleSections.expenseBreakdown && (
                    <div className="t-flex-1 sm:t-h-96">
                      <MoMBreakdown
                        startDate={filter.START_DATE}
                        endDate={filter.END_DATE}
                        reportType="EXPENSE"
                        title="MoM Expense Breakdown"
                      />
                    </div>
                  )}
                  {activeVisibleSections.topVendors && (
                    <div className="t-flex-1 sm:t-h-96">
                      <TopVendors
                        startDate={filter.START_DATE}
                        endDate={filter.END_DATE}
                      />
                    </div>
                  )}
                </div>
              )}
          </>
        );

      case GROUP_KEYS.ar:
        return activeVisibleSections.accountsReceivable ||
          activeVisibleSections.arAging ? (
          <div className="t-flex t-gap-6 t-items-stretch sm:t-flex-col">
            {activeVisibleSections.accountsReceivable && (
              <div className="t-flex-1">
                <AccountsReceivable
                  data={arData}
                  isLoading={arLoading}
                  isSuccess={arSuccess}
                  isError={arError}
                />
              </div>
            )}
            {activeVisibleSections.arAging && (
              <div className="t-flex-1">
                <ARAgingCard
                  startDate={filter.START_DATE}
                  endDate={filter.END_DATE}
                />
              </div>
            )}
          </div>
        ) : null;

      case GROUP_KEYS.ap:
        return activeVisibleSections.accountsPayable ||
          activeVisibleSections.apAging ? (
          <div className="t-flex t-gap-6 t-items-stretch sm:t-flex-col">
            {activeVisibleSections.accountsPayable && (
              <div className="t-flex-1">
                <AccountsPayable
                  data={apData}
                  isLoading={apLoading}
                  isSuccess={apSuccess}
                  isError={apError}
                />
              </div>
            )}
            {activeVisibleSections.apAging && (
              <div className="t-flex-1">
                <APAgingCard
                  startDate={filter.START_DATE}
                  endDate={filter.END_DATE}
                />
              </div>
            )}
          </div>
        ) : null;

      default:
        return null;
    }
  };

  return (
    <div className="t-flex t-flex-col t-gap-5 t-px-5 sm:t-px-3 t-pb-28">
      <div className="t-flex t-flex-col t-gap-3">
        <div className="t-flex t-items-center t-justify-between sm:t-flex-col sm:t-items-start sm:t-gap-3">
          <div className="t-flex t-items-center t-gap-2 sm:t-flex-wrap">
            <span className="t-text-body-sm t-text-text-60">
              Showing summary for:
            </span>
            <Dropdown.Root>
              <Dropdown.Trigger asChild>
                <Button size="xsmall" customType="secondary">
                  <span className="t-flex t-gap-2 t-items-center">
                    {comboboxValue?.label || "Last 6 months"}
                    <CaretDown />
                  </span>
                </Button>
              </Dropdown.Trigger>
              <Dropdown.Portal>
                <Dropdown.Content sideOffset={4} align="start">
                  {PERIOD_OPTIONS.filter((opt) => opt.value !== "custom").map(
                    (opt) => (
                      <Dropdown.Item
                        key={opt.value}
                        onSelect={() => handlePeriodChange(opt.value)}
                        className={
                          currentPeriod !== "custom" &&
                          currentPeriod === opt.value
                            ? "t-bg-surface-lighter-grey"
                            : ""
                        }
                      >
                        {opt.label}
                      </Dropdown.Item>
                    )
                  )}
                  <Dropdown.Sub>
                    <Dropdown.SubTrigger>Custom</Dropdown.SubTrigger>
                    <Dropdown.Portal>
                      <Dropdown.SubContent
                        sideOffset={5}
                        className="t-bg-white t-shadow-md t-rounded-md t-z-50 !t-p-3 t-w-[17.5rem]"
                        onFocusOutside={(e) => e.preventDefault()}
                      >
                        <Formik
                          initialValues={{
                            start_date: filter.START_DATE,
                            end_date: filter.END_DATE,
                          }}
                          onSubmit={() => {}}
                          enableReinitialize
                        >
                          {() => (
                            <div className="t-flex t-flex-col t-gap-3">
                              <div className="t-flex t-flex-col t-gap-1">
                                <span className="t-text-body-sm t-text-text-30">
                                  From
                                </span>
                                <DateInput
                                  name="start_date"
                                  value={filter.START_DATE}
                                  onChange={() => {}}
                                  onBlur={() => {}}
                                  placeholder="MMM DD, YYYY"
                                  minDate={
                                    bookkeepingStartDate
                                      ? new Date(bookkeepingStartDate)
                                      : undefined
                                  }
                                  onDateChange={handleCustomStartDateChange}
                                  customSize="small"
                                  portalId="start_date"
                                />
                              </div>
                              <div className="t-flex t-flex-col t-gap-1">
                                <span className="t-text-body-sm t-text-text-30">
                                  To
                                </span>
                                <DateInput
                                  name="end_date"
                                  value={filter.END_DATE}
                                  onChange={() => {}}
                                  onBlur={() => {}}
                                  placeholder="MMM DD, YYYY"
                                  minDate={dayjs(filter.START_DATE).toDate()}
                                  onDateChange={handleCustomEndDateChange}
                                  customSize="small"
                                  portalId="end_date"
                                />
                              </div>
                            </div>
                          )}
                        </Formik>
                      </Dropdown.SubContent>
                    </Dropdown.Portal>
                  </Dropdown.Sub>
                </Dropdown.Content>
              </Dropdown.Portal>
            </Dropdown.Root>
            <Chip isActive isRemovable={false}>
              {dayjs(filter.START_DATE).format("MMM DD, YYYY")} -{" "}
              {dayjs(filter.END_DATE).format("MMM DD, YYYY")}
            </Chip>
          </div>

          <div className="t-flex t-items-center t-gap-5">
            <Button
              size="xsmall"
              onClick={() => handleForecastToggle(!isForecastEnabled)}
              leftIcon={
                <Switch
                  name="forecast_toggle"
                  size="small"
                  checked={Boolean(isForecastEnabled)}
                  onCheckedChange={() => {}}
                />
              }
            >
              <span className="t-text-caption t-text-text-60">Forecast</span>
            </Button>

            <div className="t-flex t-items-center t-gap-3">
              <ToolTip text="Refresh">
                <Button
                  customType="icon"
                  size="xsmall"
                  onClick={handleRefresh}
                  isLoading={isResettingCache}
                >
                  <span className="t-flex t-items-center t-gap-2">
                    <img src={RefreshIcon} alt="RefreshIcon" />
                  </span>
                </Button>
              </ToolTip>
              <CustomizeSectionsTrigger
                visibleSections={activeVisibleSections}
                groupOrder={activeGroupOrder}
                onSave={handleCustomizeSave}
                sectionGroups={
                  isForecastEnabled ? FORECAST_SECTION_GROUPS : undefined
                }
                defaultVisibleSections={
                  isForecastEnabled
                    ? DEFAULT_FORECAST_VISIBLE_SECTIONS
                    : undefined
                }
                defaultGroupOrder={
                  isForecastEnabled ? DEFAULT_FORECAST_GROUP_ORDER : undefined
                }
              />
              <ToolTip text="Exports current view. To change charts, use Customize.">
                <Button
                  customType="icon"
                  size="xsmall"
                  onClick={handleExportClick}
                >
                  <span className="t-flex t-items-center t-gap-2">
                    <DownloadIcon />
                  </span>
                </Button>
              </ToolTip>
            </div>
          </div>
        </div>

        {isForecastEnabled && (
          <ForecastToolbar filter={filter} updateFilter={updateFilter} />
        )}
      </div>

      {activeGroupOrder.map((groupKey) => (
        <div key={groupKey} className="t-flex t-flex-col t-gap-5">
          {renderGroup(groupKey)}
        </div>
      ))}
    </div>
  );
};
